import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, Output, EventEmitter, Optional, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

@Component({
  selector: 'app-text-area',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="relative">
      <textarea
        #textareaRef
        [placeholder]="placeholder"
        [rows]="rows"
        [disabled]="disabled"
        [value]="_value"
        (input)="onInput($event)"
        (blur)="onTouched()"
        [ngClass]="textareaClasses"
      ></textarea>
      @if (hint) {
        <p class="mt-2 text-sm"
          [ngClass]="error ? 'text-error-500' : 'text-gray-500 dark:text-gray-400'">
          {{ hint }}
        </p>
      }
    </div>
  `,
})
export class TextAreaComponent implements ControlValueAccessor {

  @ViewChild('textareaRef', { static: true }) textareaRef!: ElementRef<HTMLTextAreaElement>;

  @Input() placeholder = 'Enter your message';
  @Input() rows = 3;
  @Input() className = '';
  @Input() disabled = false;
  @Input() error = false;
  @Input() hint = '';

  // Keep @Input() value so [value]="..." still works in other templates
  @Input() set value(val: string) {
    this._value = val ?? '';
  }
  get value(): string {
    return this._value;
  }
  _value = '';

  // Keep (valueChange) so old templates still work
  @Output() valueChange = new EventEmitter<string>();

  private onChange: (val: string) => void = () => {};
  onTouched: () => void = () => {};

  constructor(@Optional() @Self() private ngControl: NgControl) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  // ControlValueAccessor
  writeValue(val: string): void {
    this._value = val ?? '';
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event) {
    const val = (event.target as HTMLTextAreaElement).value;
    this._value = val;
    this.onChange(val);        // for ngModel / reactive forms
    this.valueChange.emit(val); // for old (valueChange)="..." bindings
  }

  /** Inserts text at (or replacing) the given selection range, then restores
   *  focus and cursor placement right after the inserted text. Falls back to
   *  the end of the current value when no range is given (e.g. focus was lost
   *  to an external picker). Uses execCommand so the native 'input' event
   *  fires and onInput() stays the single source of truth for value updates. */
  insertAtCursor(text: string, selStart?: number, selEnd?: number): void {
    const el = this.textareaRef.nativeElement;
    const start = selStart ?? this._value.length;
    const end = selEnd ?? this._value.length;
    el.focus();
    el.setSelectionRange(start, end);
    document.execCommand('insertText', false, text);
  }

  get textareaClasses(): string {
    let base = `w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-hidden ${this.className} `;
    if (this.disabled) {
      base += 'bg-gray-100 opacity-50 text-gray-500 border-gray-300 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
    } else if (this.error) {
      base += 'bg-transparent border-gray-300 focus:border-error-300 focus:ring-3 focus:ring-error-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-error-800';
    } else {
      base += 'bg-transparent text-gray-900 border-gray-300 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800';
    }
    return base;
  }
}